import Feature, { FeatureLike } from "ol/Feature";
import { Point } from "ol/geom";
import { getPointResolution } from "ol/proj";
import { IScaleDepLabel } from "./config";
import { getMap } from "./GeoLiteMap";
import { replaceFieldsWithValues } from "./utils";

export const clusterExpand = (clones: FeatureLike[], features: FeatureLike[], center: number[], duration?: number) =>
    clones.map((f, i) => animatePoint(f, center, (features[i].getGeometry() as Point).getCoordinates(), duration));

export const clusterCircularExpand = (features: Feature[], center: number[], duration, radius = 16) => {
    const view = getMap().getView();
    const pointResolution = getPointResolution(view.getProjection(), view.getResolution(), center, "m");

    const pointRadius = Math.floor(radius * pointResolution);
    const count = features.length;
    const circumference = 2.1 * pointRadius * count;
    const distanceFromCenter = circumference / (2 * Math.PI);
    const minDistance = Math.floor(2.5 * radius * pointResolution);
    const distance = distanceFromCenter > minDistance ? distanceFromCenter : minDistance;
    const angle = (2 * Math.PI) / count;

    //@ts-ignore
    const scale = document.mapScale;
    const labelsConfigForScale = features[0]
        .get("layerData")
        ?.vectorStyles?.labels?.find((hl: IScaleDepLabel) => hl.maxScale >= scale && scale > hl.minScale);

    const isLabelsCircular = features.some(
        (f) => f.get("layerData")?.cluster?.hoverAnimation?.circularExpandLabelsCircular
    );

    features.map((f, i) => {
        const currentAngle = angle * (i + 1);
        let labelSize: number[];
        let labelTargetCoordinates: number[];
        if (isLabelsCircular) {
            labelSize = f.get("LABELSIZEPX") || getLabelSizePx(f, labelsConfigForScale);
            labelTargetCoordinates = calculateLabelCoordinates(
                center,
                distance * 1.8,
                currentAngle,
                Math.floor(labelSize[0] * pointResolution),
                Math.floor(labelSize[1] * pointResolution)
            );
        }

        animatePoint(
            f,
            center,
            calculatePointCoordinates(center, distance, currentAngle),
            duration,
            labelTargetCoordinates
        );
    });
};

export const animatePoint = (feature, startCoords, targetCoords, duration = 200, labelTargetCoords = null) => {
    const startTime = new Date().getTime();

    function animate() {
        const currentTime = new Date().getTime();
        const progress = Math.min(1, (currentTime - startTime) / duration);
        const deltaX = (targetCoords[0] - startCoords[0]) * progress;
        const deltaY = (targetCoords[1] - startCoords[1]) * progress;
        const newPosition = [startCoords[0] + deltaX, startCoords[1] + deltaY];

        feature.getGeometry().setCoordinates(newPosition);

        if (labelTargetCoords) {
            const dX = (labelTargetCoords[0] - startCoords[0]) * progress;
            const dY = (labelTargetCoords[1] - startCoords[1]) * progress;
            const newPosition = [startCoords[0] + dX, startCoords[1] + dY];
            feature.set("LABELCOORDS", newPosition, true);
        }

        if (progress < 1) {
            requestAnimationFrame(animate);
        }
    }

    requestAnimationFrame(animate);
};

const calculatePointCoordinates = (center, distance, angleRad): number[] => [
    center[0] + distance * Math.cos(angleRad),
    center[1] + distance * Math.sin(angleRad)
];
const calculateLabelCoordinates = (center, distance, angleRad, labelWidth, labelHeight): number[] => {
    const deltaX = -0.5 * (1 - Math.cos(angleRad)) * labelWidth;
    const deltaY = 0.5 + 0.5 * Math.sin(angleRad) * labelHeight;

    return [center[0] + distance * Math.cos(angleRad) + deltaX, center[1] + distance * Math.sin(angleRad) + deltaY];
};

const getTextLabelSize = (text, font) => {
    const hiddenLabel = document.getElementById("hiddenLabel");
    hiddenLabel.style.font = font;
    hiddenLabel.innerHTML = text?.replaceAll("\n", "<br>");

    return [hiddenLabel.offsetWidth + 7, hiddenLabel.offsetHeight + 6]; // 7 add padding summed from both sides
};

const getLabelSizePx = (feature: Feature, labelsConfigForScale) => {
    const text = replaceFieldsWithValues(feature, labelsConfigForScale?.labels[0]?.labelText?.text);
    const labelSizePx = getTextLabelSize(text, labelsConfigForScale?.labels[0]?.labelText?.font);
    feature.set("LABELSIZEPX", labelSizePx, true);

    return labelSizePx;
};
